import { useQuery } from "@apollo/client";
import { GET_TRANSCRIPT } from "apollo";
import { LoadingLayout, PageLayout, Separator, Text } from "components";
import {
  CheckIcon,
  CopyClipboardTextIcon,
  HourglassSimpleLowIcon,
} from "components/icons";
import { format } from "date-fns";
import { Link, useParams } from "react-router-dom";
import { ItemLabel, TranscriptSpeakerItem } from "../components";
import { extractFromQuery, formatTimeToAM } from "utils/helpers";
import {
  FullTranscriptStyle,
  MeetingGridStyle,
  ScreenContainerStyle,
  TranscriptSummaryStyle,
} from "../styles";
import { TranscriptResponse } from "types";
import { useState } from "react";
import { E_Route } from "utils/constants";

const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);

export const MeetingTranscriptScreen = () => {
  // get meeting id from url params
  const { id } = useParams<{ id: string }>();
  const [copied, setCopied] = useState(false);

  const { internal } = extractFromQuery<{ internal?: "yes" | "no" }>(
    window.location.search,
  );

  const { data: transcriptData, loading } = useQuery<TranscriptResponse>(
    GET_TRANSCRIPT,
    {
      variables: { id },
    },
  );

  if (loading) {
    return <LoadingLayout />;
  }

  const copyTranscriptToClipboard = () => {
    const transcriptText = fullTranscript
      .map((item) => `${item.speaker}: ${item.text}`)
      .join("\n");
    navigator.clipboard.writeText(transcriptText);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  const fullTranscript: {
    speaker: string;
    text: string;
  }[] = transcriptData?.transcript?.transcript
    ? JSON.parse(transcriptData?.transcript?.transcript)
    : null;

  return (
    <PageLayout title={`Transcript | Multiplayer`}>
      <div className={ScreenContainerStyle}>
        <div className={TranscriptSummaryStyle}>
          <Text.H2>
            {transcriptData?.transcript?.meetingTitle || "Ad hoc meeting"}
          </Text.H2>

          <Separator.Horizontal spacing={5} />

          <div className={MeetingGridStyle}>
            <ItemLabel
              Icon={HourglassSimpleLowIcon}
              label="Time and Date"
              value={`${format(new Date(transcriptData?.transcript?.created || Date.now()), "EEEE, MMMM d")}`}
              valueExtra={
                transcriptData?.transcript?.meetingDate
                  ? `${formatTimeToAM(new Date(transcriptData?.transcript?.meetingDate))}`
                  : "/"
              }
            />

            <Separator.Vertical spacing={10} />
          </div>

          {!internal && transcriptData?.transcript?.meetingId && (
            <Link
              to={`${E_Route.Meeting}/${transcriptData?.transcript?.meetingId}`}
              style={{
                textDecoration: "none",
              }}
            >
              <Text.BodyXS color="highlight/1">See Meeting prep</Text.BodyXS>
            </Link>
          )}

          <Separator.Horizontal spacing={2.5} />

          <div
            dangerouslySetInnerHTML={{
              __html: transcriptData?.transcript?.detailedSummary || "",
            }}
          />

          <Separator.Horizontal spacing={1} />
        </div>
        {fullTranscript && (
          <div className={FullTranscriptStyle}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text.H3>Transcript</Text.H3>

              {copied ? (
                <CheckIcon fill="#fff" />
              ) : (
                <div onClick={copyTranscriptToClipboard}>
                  <CopyClipboardTextIcon
                    fill="primary"
                    style={{
                      width: "24px",
                      height: "24px",
                      cursor: "pointer",
                    }}
                  />
                </div>
              )}
            </div>
            <Separator.Horizontal spacing={5} />
            {fullTranscript.map((item, idx) => (
              <TranscriptSpeakerItem
                key={idx}
                speaker={item.speaker}
                text={item.text}
              />
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
};
